import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminTopBar, ViewScroll, Panel, StatCard, Sym } from '../../ui';
import { fetchNotices, createNotice } from '../../api/notices';
import { fetchUsers } from '../../api/users';
import {
  UserRole,
  type Notice,
  type NoticeChannel,
  type NoticeTarget,
  type CreateNoticeInput,
  type UserInfo,
} from '@grosync/common';

/* ============================================================================
 * 공지 — 앱·웹 전체 알림 발송
 * 발송 이력·도달 회원 수는 백엔드 notices API(/api/notices)에서 가져온다.
 * 대상 회원 수는 users API에서 실시간 집계한다 (서버 reach 로직과 동일).
 * 라이브 미리보기는 프로토타입과 동일하게 클라이언트에서 계산한다.
 * ========================================================================= */

type CSS = React.CSSProperties;

type NoticeType = '일반' | '점검' | '긴급' | '업데이트';
interface NcTypeMeta {
  k: NoticeType;
  icon: string;
  tone: string;
  bg: string;
  fg: string;
}

const NC_TYPES: NcTypeMeta[] = [
  { k: '일반', icon: 'campaign', tone: 'var(--gs-secondary-40)', bg: 'var(--md-sys-color-secondary-container)', fg: 'var(--md-sys-color-on-secondary-container)' },
  { k: '점검', icon: 'build', tone: 'var(--gs-tertiary-40)', bg: 'var(--md-sys-color-tertiary-container)', fg: 'var(--md-sys-color-on-tertiary-container)' },
  { k: '긴급', icon: 'warning', tone: 'var(--gs-warning)', bg: 'var(--gs-warning-container)', fg: 'var(--gs-on-warning-container)' },
  { k: '업데이트', icon: 'system_update', tone: 'var(--gs-primary-40)', bg: 'var(--md-sys-color-primary-container)', fg: 'var(--md-sys-color-on-primary-container)' },
];
const ncType = (k: NoticeType): NcTypeMeta => NC_TYPES.find((t) => t.k === k) ?? NC_TYPES[0];

/** 화면 대상 라벨 ↔ 서버 NoticeTarget */
const TARGET_LABELS: Record<NoticeTarget, string> = {
  all: '전체 회원',
  users: '사용자',
  managers: '담당자',
};

/** ISO 문자열 → "MM.DD HH:mm" 표기 */
const fmtAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(d.getMonth() + 1)}.${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ncInput: CSS = {
  width: '100%',
  height: 48,
  padding: '0 14px',
  borderRadius: 12,
  boxSizing: 'border-box',
  border: '1.5px solid var(--md-sys-color-outline-variant)',
  background: 'var(--md-sys-color-surface-container-low)',
  font: '500 15px/22px var(--gs-font-kr)',
  color: 'var(--md-sys-color-on-surface)',
  outline: 'none',
};

function NcField({ children }: { children: React.ReactNode }): React.ReactElement {
  return <div style={{ marginBottom: 18 }}>{children}</div>;
}
function NcLabel({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div style={{ font: '600 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginBottom: 8 }}>
      {children}
    </div>
  );
}

interface NcChToggleProps {
  icon: string;
  label: string;
  desc: string;
  on: boolean;
  onClick: () => void;
}
function NcChToggle({ icon, label, desc, on, onClick }: NcChToggleProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        textAlign: 'left',
        cursor: 'pointer',
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '13px 15px',
        borderRadius: 14,
        border: on ? '2px solid var(--md-sys-color-primary)' : '1.5px solid var(--md-sys-color-outline-variant)',
        background: on ? 'var(--md-sys-color-primary-container)' : 'var(--md-sys-color-surface-container-lowest)',
      }}
    >
      <Sym
        name={icon}
        size={22}
        fill={on ? 1 : 0}
        style={{ color: on ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)' }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ font: '700 14px/18px var(--gs-font-kr)', color: on ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface)' }}>
          {label}
        </div>
        <div style={{ font: '400 11px/15px var(--gs-font-kr)', color: on ? 'var(--md-sys-color-on-primary-container)' : 'var(--md-sys-color-on-surface-variant)', opacity: 0.9 }}>
          {desc}
        </div>
      </div>
      <span
        style={{
          width: 42,
          height: 25,
          borderRadius: 9999,
          flexShrink: 0,
          padding: 3,
          display: 'inline-flex',
          background: on ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-highest)',
          justifyContent: on ? 'flex-end' : 'flex-start',
          alignItems: 'center',
        }}
      >
        <span style={{ width: 19, height: 19, borderRadius: 9999, background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.3)' }} />
      </span>
    </button>
  );
}

interface TargetOpt {
  k: NoticeTarget;
  label: string;
  n: number;
}

export default function NoticeView(): React.ReactElement {
  const qc = useQueryClient();
  const noticesQ = useQuery({ queryKey: ['notices'], queryFn: fetchNotices });
  const usersQ = useQuery({ queryKey: ['users'], queryFn: fetchUsers });

  const [type, setType] = useState<NoticeType>('일반');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ch, setCh] = useState<{ app: boolean; web: boolean }>({ app: true, web: true });
  const [target, setTarget] = useState<NoticeTarget>('all');
  const [timing, setTiming] = useState<'now' | 'schedule'>('now');
  const [at, setAt] = useState('2026-06-14T09:00');
  const [toast, setToast] = useState<string | null>(null);

  // 실제 사용자 기반 대상 회원 수 (서버 reach 로직: active 기준)
  const counts = useMemo(() => {
    const users: UserInfo[] = usersQ.data ?? [];
    const active = users.filter((u) => u.isActive);
    return {
      all: active.length,
      users: active.filter((u) => u.role === UserRole.VIEWER).length,
      managers: active.filter((u) => u.role === UserRole.OPERATOR).length,
    };
  }, [usersQ.data]);

  const targets: TargetOpt[] = [
    { k: 'all', label: TARGET_LABELS.all, n: counts.all },
    { k: 'users', label: TARGET_LABELS.users, n: counts.users },
    { k: 'managers', label: TARGET_LABELS.managers, n: counts.managers },
  ];

  const notices = noticesQ.data ?? [];

  // StatCards
  const stats = useMemo(() => {
    const now = new Date();
    const monthCount = notices.filter((n) => {
      const d = new Date(n.createdAt);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
    const sent = notices.filter((n) => n.status === 'sent');
    const lastReach = sent[0]?.reach ?? 0;
    const openRateSum = sent.reduce((s, n) => s + (n.reach > 0 ? n.openCount / n.reach : 0), 0);
    const avgOpenRate = sent.length > 0 ? Math.round((openRateSum / sent.length) * 100) : 0;
    return { monthCount, lastReach, avgOpenRate };
  }, [notices]);

  const tMeta = ncType(type);
  const targetN = targets.find((t) => t.k === target)?.n ?? 0;
  const targetLabel = TARGET_LABELS[target];
  const canSend = title.trim().length > 0 && body.trim().length > 0 && (ch.app || ch.web);

  const createM = useMutation({
    mutationFn: (input: CreateNoticeInput) => createNotice(input),
    onSuccess: (created) => {
      void qc.invalidateQueries({ queryKey: ['notices'] });
      setToast(
        created.status === 'scheduled'
          ? `${fmtAt(created.scheduledAt ?? created.createdAt)}에 발송 예약되었습니다.`
          : `${TARGET_LABELS[created.target]} ${created.reach.toLocaleString()}명에게 공지를 발송했습니다.`,
      );
      setTitle('');
      setBody('');
      window.setTimeout(() => setToast(null), 4000);
    },
    onError: (err: unknown) => {
      setToast(`공지 발송에 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
      window.setTimeout(() => setToast(null), 4000);
    },
  });

  const send = (): void => {
    if (!canSend || createM.isPending) return;
    const channels: NoticeChannel[] = [];
    if (ch.app) channels.push('app');
    if (ch.web) channels.push('web');
    const input: CreateNoticeInput = {
      type,
      title: title.trim(),
      content: body.trim(),
      channels,
      target,
      scheduledAt: timing === 'schedule' ? new Date(at).toISOString() : null,
    };
    createM.mutate(input);
  };

  const HIST_COLS = '0.7fr 2.4fr 0.9fr 0.9fr 1fr 0.7fr 0.7fr';

  return (
    <>
      <AdminTopBar title="공지" subtitle="앱 · 웹 사용자에게 전체 알림을 발송합니다" />

      <ViewScroll gap={18}>
        {toast && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '13px 18px',
              borderRadius: 14,
              background: 'var(--md-sys-color-primary-container)',
              color: 'var(--md-sys-color-on-primary-container)',
              font: '600 14px/20px var(--gs-font-kr)',
            }}
          >
            <Sym name="check_circle" size={20} fill={1} />
            {toast}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16 }}>
          <StatCard icon="campaign" label="이번 달 공지" value={stats.monthCount} unit="건" tone="secondary" sub={`누적 ${notices.length}건`} />
          <StatCard icon="group" label="도달 회원" value={stats.lastReach.toLocaleString()} unit="명" tone="primary" sub="최근 공지 도달 기준" />
          <StatCard icon="mark_email_read" label="평균 열람률" value={stats.avgOpenRate} unit="%" tone="muted" sub="발송 완료 공지 기준" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.25fr) minmax(320px, 0.75fr)', gap: 18, alignItems: 'start' }}>
          {/* compose */}
          <Panel title="새 공지 작성" subtitle="앱과 웹에 동시 발송할 수 있습니다">
            <NcField>
              <NcLabel>공지 유형</NcLabel>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {NC_TYPES.map((t) => {
                  const a = type === t.k;
                  return (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setType(t.k)}
                      style={{
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        height: 38,
                        padding: '0 14px',
                        borderRadius: 9999,
                        border: a ? 'none' : '1.5px solid var(--md-sys-color-outline-variant)',
                        background: a ? t.bg : 'transparent',
                        color: a ? t.fg : 'var(--md-sys-color-on-surface-variant)',
                        font: `${a ? 700 : 500} 13px/1 var(--gs-font-kr)`,
                      }}
                    >
                      <Sym name={t.icon} size={16} fill={a ? 1 : 0} style={{ color: a ? t.tone : 'inherit' }} />
                      {t.k}
                    </button>
                  );
                })}
              </div>
            </NcField>

            <NcField>
              <NcLabel>제목</NcLabel>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={50}
                placeholder="공지 제목을 입력하세요"
                style={ncInput}
              />
            </NcField>

            <NcField>
              <NcLabel>내용</NcLabel>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                maxLength={500}
                rows={5}
                placeholder="공지 내용을 입력하세요. 앱 푸시·웹 알림에 동일하게 표시됩니다."
                style={{ ...ncInput, height: 'auto', padding: '12px 14px', lineHeight: '22px', resize: 'vertical' }}
              />
              <div
                style={{
                  textAlign: 'right',
                  font: '400 11px/14px var(--gs-font-kr)',
                  color: 'var(--md-sys-color-on-surface-variant)',
                  marginTop: 5,
                }}
              >
                <span className="gs-num">{body.length}</span>/500
              </div>
            </NcField>

            <NcField>
              <NcLabel>발송 채널</NcLabel>
              <div style={{ display: 'flex', gap: 10 }}>
                <NcChToggle icon="smartphone" label="앱 푸시 알림" desc="사용자 모바일 앱" on={ch.app} onClick={() => setCh((c) => ({ ...c, app: !c.app }))} />
                <NcChToggle icon="desktop_windows" label="웹 알림" desc="관리자·웹 대시보드" on={ch.web} onClick={() => setCh((c) => ({ ...c, web: !c.web }))} />
              </div>
            </NcField>

            <NcField>
              <NcLabel>발송 대상</NcLabel>
              <div style={{ display: 'flex', gap: 8 }}>
                {targets.map((t) => {
                  const a = target === t.k;
                  return (
                    <button
                      key={t.k}
                      type="button"
                      onClick={() => setTarget(t.k)}
                      style={{
                        flex: 1,
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 3,
                        padding: '11px 14px',
                        borderRadius: 12,
                        border: a ? '2px solid var(--md-sys-color-primary)' : '1.5px solid var(--md-sys-color-outline-variant)',
                        background: a ? 'var(--md-sys-color-surface-container-low)' : 'var(--md-sys-color-surface-container-lowest)',
                      }}
                    >
                      <span style={{ font: '600 13px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>{t.label}</span>
                      <span className="gs-num" style={{ font: '700 17px/20px var(--gs-font-kr)', color: a ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface)' }}>
                        {t.n.toLocaleString()}
                        <span style={{ font: '400 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginLeft: 2 }}>명</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </NcField>

            <NcField>
              <NcLabel>발송 시점</NcLabel>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'inline-flex', padding: 4, borderRadius: 9999, background: 'var(--md-sys-color-surface-container-high)' }}>
                  {([['now', '즉시 발송'], ['schedule', '예약 발송']] as const).map(([k, l]) => {
                    const a = timing === k;
                    return (
                      <button
                        key={k}
                        type="button"
                        onClick={() => setTiming(k)}
                        style={{
                          height: 34,
                          padding: '0 16px',
                          borderRadius: 9999,
                          border: 0,
                          cursor: 'pointer',
                          background: a ? 'var(--md-sys-color-surface-container-lowest)' : 'transparent',
                          color: a ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-on-surface-variant)',
                          font: `${a ? 700 : 500} 13px/1 var(--gs-font-kr)`,
                          boxShadow: a ? 'var(--md-sys-elevation-1)' : 'none',
                        }}
                      >
                        {l}
                      </button>
                    );
                  })}
                </div>
                {timing === 'schedule' && (
                  <input
                    type="datetime-local"
                    value={at}
                    onChange={(e) => setAt(e.target.value)}
                    style={{ ...ncInput, width: 'auto', height: 42, flex: 1, minWidth: 200 }}
                  />
                )}
              </div>
            </NcField>

            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              style={{
                width: '100%',
                height: 52,
                borderRadius: 14,
                border: 0,
                marginTop: 4,
                background: canSend ? 'var(--md-sys-color-primary)' : 'var(--md-sys-color-surface-container-high)',
                color: canSend ? 'var(--md-sys-color-on-primary)' : 'var(--md-sys-color-on-surface-variant)',
                font: '700 15px/22px var(--gs-font-kr)',
                cursor: canSend ? 'pointer' : 'default',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Sym name={timing === 'now' ? 'send' : 'schedule_send'} size={20} />
              {timing === 'now' ? `${targetLabel} ${targetN.toLocaleString()}명에게 발송` : '발송 예약'}
            </button>
          </Panel>

          {/* preview */}
          <Panel title="미리보기" subtitle="수신자 화면">
            <div style={{ font: '600 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', marginBottom: 9 }}>
              앱 푸시 알림
            </div>
            <div
              style={{
                borderRadius: 18,
                padding: '13px 14px',
                background: 'var(--md-sys-color-surface-container-high)',
                display: 'flex',
                gap: 11,
                opacity: ch.app ? 1 : 0.4,
              }}
            >
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 10,
                  flexShrink: 0,
                  background: 'var(--md-sys-color-primary-container)',
                  color: 'var(--md-sys-color-on-primary-container)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Sym name="eco" size={22} fill={1} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ font: '700 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)' }}>GroVision</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', height: 16, padding: '0 6px', borderRadius: 9999, background: tMeta.bg, color: tMeta.fg, font: '600 9px/1 var(--gs-font-kr)' }}>
                    {type}
                  </span>
                  <span style={{ flex: 1 }} />
                  <span style={{ font: '400 10px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>지금</span>
                </div>
                <div style={{ font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', marginTop: 3, textWrap: 'pretty' }}>
                  {title || '공지 제목 미리보기'}
                </div>
                <div
                  style={{
                    font: '400 12px/17px var(--gs-font-kr)',
                    color: 'var(--md-sys-color-on-surface-variant)',
                    marginTop: 2,
                    textWrap: 'pretty',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {body || '공지 내용이 여기에 표시됩니다.'}
                </div>
              </div>
            </div>

            <div style={{ font: '600 11px/14px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)', margin: '18px 0 9px' }}>
              웹 알림 배너
            </div>
            <div
              style={{
                borderRadius: 14,
                padding: '12px 14px',
                display: 'flex',
                gap: 11,
                alignItems: 'flex-start',
                background: tMeta.bg,
                color: tMeta.fg,
                opacity: ch.web ? 1 : 0.4,
              }}
            >
              <Sym name={tMeta.icon} size={20} fill={1} style={{ color: tMeta.tone, flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ font: '700 13px/18px var(--gs-font-kr)', textWrap: 'pretty' }}>{title || '공지 제목 미리보기'}</div>
                <div
                  style={{
                    font: '400 12px/17px var(--gs-font-kr)',
                    marginTop: 2,
                    opacity: 0.9,
                    textWrap: 'pretty',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {body || '공지 내용이 여기에 표시됩니다.'}
                </div>
              </div>
            </div>

            <div
              style={{
                marginTop: 16,
                padding: '12px 14px',
                borderRadius: 12,
                background: 'var(--md-sys-color-surface-container-low)',
                border: '1px solid var(--md-sys-color-outline-variant)',
                font: '400 12px/18px var(--gs-font-kr)',
                color: 'var(--md-sys-color-on-surface-variant)',
              }}
            >
              <Sym name="info" size={14} style={{ verticalAlign: '-2px', marginRight: 4, color: 'var(--md-sys-color-secondary)' }} />
              <b style={{ color: 'var(--md-sys-color-on-surface)' }}>
                {targetLabel} {targetN.toLocaleString()}명
              </b>
              에게
              {ch.app && ch.web ? ' 앱·웹' : ch.app ? ' 앱' : ch.web ? ' 웹' : ' (채널 미선택)'} 으로 발송됩니다.
            </div>
          </Panel>
        </div>

        {/* history */}
        <Panel title="발송 이력" subtitle={`최근 ${notices.length}건`} pad={false}>
          <div className="gs-scroll-hide" style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 760 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: HIST_COLS,
                  padding: '12px 22px',
                  borderBottom: '1px solid var(--md-sys-color-outline-variant)',
                  background: 'var(--md-sys-color-surface-container-low)',
                }}
              >
                {['유형', '제목', '채널', '대상', '발송일시', '도달', '열람'].map((h, i) => (
                  <span
                    key={h}
                    style={{
                      font: '600 12px/16px var(--gs-font-kr)',
                      color: 'var(--md-sys-color-on-surface-variant)',
                      textAlign: i >= 5 ? 'right' : 'left',
                    }}
                  >
                    {h}
                  </span>
                ))}
              </div>
              {noticesQ.isLoading && (
                <div style={{ padding: '28px 22px', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  불러오는 중…
                </div>
              )}
              {!noticesQ.isLoading && notices.length === 0 && (
                <div style={{ padding: '28px 22px', textAlign: 'center', font: '400 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>
                  발송 이력이 없습니다
                </div>
              )}
              {notices.map((r: Notice, i) => {
                const tm = ncType(r.type);
                const openRate = r.reach > 0 ? Math.round((r.openCount / r.reach) * 100) : 0;
                return (
                  <div
                    key={r.id}
                    className="gs-row"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: HIST_COLS,
                      padding: '13px 22px',
                      alignItems: 'center',
                      borderBottom: i === notices.length - 1 ? 0 : '1px solid var(--md-sys-color-outline-variant)',
                    }}
                  >
                    <span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 22, padding: '0 9px', borderRadius: 9999, background: tm.bg, color: tm.fg, font: '600 11px/1 var(--gs-font-kr)' }}>
                        <Sym name={tm.icon} size={12} fill={1} />
                        {r.type}
                      </span>
                    </span>
                    <span style={{ font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 12 }}>
                      {r.status === 'scheduled' && <Sym name="schedule" size={13} style={{ verticalAlign: '-2px', marginRight: 4, color: 'var(--gs-warning)' }} />}
                      {r.title}
                    </span>
                    <span style={{ display: 'flex', gap: 4 }}>
                      {r.channels.includes('app') && <Sym name="smartphone" size={16} style={{ color: 'var(--md-sys-color-primary)' }} />}
                      {r.channels.includes('web') && <Sym name="desktop_windows" size={16} style={{ color: 'var(--md-sys-color-secondary)' }} />}
                    </span>
                    <span style={{ font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{TARGET_LABELS[r.target]}</span>
                    <span className="gs-num" style={{ font: '500 12px/16px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface-variant)' }}>{fmtAt(r.scheduledAt ?? r.createdAt)}</span>
                    <span className="gs-num" style={{ font: '600 13px/18px var(--gs-font-kr)', color: 'var(--md-sys-color-on-surface)', textAlign: 'right' }}>{r.reach.toLocaleString()}</span>
                    <span
                      className="gs-num"
                      style={{
                        font: '600 13px/18px var(--gs-font-kr)',
                        textAlign: 'right',
                        color: r.status === 'scheduled' ? 'var(--gs-warning)' : 'var(--md-sys-color-on-surface)',
                      }}
                    >
                      {r.status === 'scheduled' ? '예약' : `${openRate}%`}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Panel>
      </ViewScroll>
    </>
  );
}
